import axios from "axios";
import * as types from "./types";
import { getCredentials } from "@/helpers/credentials";
import { ADMIN_CREDENTIALS_STORAGE_KEY } from "@/constants/auth";

let kpiClient = undefined;
let subscriptionPackagesCache = null;

const getClientInstance = () => {
  if (!kpiClient) {
    kpiClient = axios.create({
      baseURL: process.env.VUE_APP_KPI_ENDPOINT,
      withCredentials: false,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
      },
    });

    // Add interceptor to dynamically add token on each request
    kpiClient.interceptors.request.use(
      config => {
        const credentials = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY);
        if (credentials?.token) {
          config.headers.Authorization = `Bearer ${credentials.token}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );
  }
  return kpiClient;
};

export default {
  [types.SAVE_PLANNED_OKR]: ({}, payload) => {
    return getClientInstance()
      .post("/api/planned_okr", payload)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },

  [types.GET_PLANNED_OKR]: ({}, payload) => {
    return getClientInstance()
      .post("/api/planned_okr/by_consultant", payload)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },

  [types.GET_PLANNED_OKR_TOTALIZER]: ({ commit }, payload) => {
    return getClientInstance()
      .post("/api/planned_okr/totalizer", payload)
      .then((resp) => {
        commit(types.MUTATE_PLANNED_OKR_TOTALIZER, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => Promise.reject(err));
  },

  [types.GET_CONSULTANT_EP_PERCENTAGE]: ({}, payload) => {
    return getClientInstance()
      .get(`api/planned_okr/consultant_ep_percentage/${payload}`)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },

  [types.GET_SCOPE_OPTIONS]: ({}) => {
    return getClientInstance()
      .get("api/planned_okr/scope_options")
      .then((resp) => Promise.resolve(resp.data))
      .catch((err) => {
        console.error("Erro ao carregar scope_options:", err);
        return Promise.resolve(["own"]); // fallback
      });
  },

  [types.GET_MONTHS_INTERVAL]: ({}) => {
    return getClientInstance()
      .get("api/planned_okr/quarters_interval")
      .then((resp) => Promise.resolve(resp.data))
      .catch((err) => {
        console.error("Erro ao carregar quarters_interval:", err);
        return Promise.resolve({
          initial_year: 2022,
          initial_quarter: 1,
          end_year: 2026,
          end_quarter: 2,
        });
      });
  },
  [types.GET_RANGE_DATES]: ({}, payload) => {
    return getClientInstance()
      .get(`/api/planned_okr/quarters_interval`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SEARCH_PLANNED_OKR_LIST]: ({ commit }, payload) => {
    const resource = `/api/planned_okr/consultants_list?page=${payload.currentPage}`;
    delete payload.currentPage;
    return getClientInstance()
      .post(resource, payload)
      .then((resp) => {
        commit(types.MUTATE_PLANNED_OKR_LIST, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_PRO_LABORE_TOTAL]: ({ commit }, payload) => {
    return getClientInstance()
      .post("/api/realized_okr/prolabore_data", payload)
      .then((resp) => {
        return Promise.resolve(resp.data);
      })
      .catch((err) => Promise.reject(err));
  },

  [types.GET_GOALS_FUNNEL_GRAPH_DATA]: ({}, payload) => {
    const body = payload || {};
    return getClientInstance()
      .post("/api/realized_okr/goals_funnel_graph_data", body)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },

  [types.GET_INTERVIEW_EFFICIENCY_GRAPH_DATA]: ({ commit }, payload) => {
    const body = payload || {};
    const key = JSON.stringify({
      y: String(body.year),
      q: String(body.quarter),
      t: body.structure_type,
    });

    return getClientInstance()
      .post("/api/realized_okr/interview_efficiency_graph_data", body)
      .then((resp) => {
        commit(types.MUTATE_INTERVIEW_EFFICIENCY_BY_KEY, { key, data: resp.data });
        return Promise.resolve(resp.data);
      })
      .catch((err) => Promise.reject(err));
  },

  [types.GET_CURRENT_PERFORMANCE_TABLE_DATA]: ({}, payload) => {
    const body = payload || {};
    return getClientInstance()
      .post("/api/realized_okr/current_performance_table_data", body)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
  [types.GET_OKR_OVERVIEW_TOTALIZER]: ({ commit }, payload) => {
    return getClientInstance()
      .post("/api/realized_okr/consultant_overview_totalizer", payload)
      .then((resp) => {
        commit(types.MUTATE_OKR_OVERVIEW_TOTALIZER, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => Promise.reject(err));
  },
  [types.SEARCH_OKR_OVERVIEW_LIST]: ({ commit }, payload) => {
    const resource = `/api/realized_okr/consultant_overview_list?page=${payload.currentPage}`;
    delete payload.currentPage;
    return getClientInstance()
      .post(resource, payload)
      .then((resp) => {
        commit(types.MUTATE_OKR_OVERVIEW_LIST, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => Promise.reject(err));
  },
  [types.GET_CURRENT_PERFORMANCE_CONSULTANT]: ({}, payload) => {
    const body = payload || {};
    return getClientInstance()
      .post("/api/realized_okr/current_performance_consultant", body)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
  [types.GET_HISTORICAL_PERFORMANCE]: ({ commit }, payload) => {
    return getClientInstance()
      .post("/api/realized_okr/historical_performance_table_data", payload)
      .then((resp) => {
        commit(types.MUTATE_HISTORICAL_PERFORMANCE, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => Promise.reject(err));
  },
  [types.GET_HAS_FILLED_OKR]: ({ }, payload) => {
    return getClientInstance()
      .post("/api/planned_okr/has_filled_okr", payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => Promise.reject(err));
  },
  [types.GET_OKR_PREMISES]: ({}, { consultantId, year, quarter }) => {
    return getClientInstance()
      .get(`/api/planned_okr/premises/${consultantId}`, { params: { year, quarter } })
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
  [types.UPDATE_OKR_PREMISES]: ({}, { consultantId, payload }) => {
    return getClientInstance()
      .put(`/api/planned_okr/premises/${consultantId}`, payload)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
  [types.GET_BONUS_TIERS]: ({}, consultantId) => {
    return getClientInstance()
      .get(`/api/planned_okr/bonus_tiers/${consultantId}`)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
  [types.GET_SUBSCRIPTION_PACKAGES]: ({}) => {
    // lista global e estável — 1 chamada por sessão
    if (subscriptionPackagesCache) {
      return Promise.resolve(subscriptionPackagesCache);
    }
    return getClientInstance()
      .get("/api/planned_okr/subscription_packages")
      .then((resp) => {
        subscriptionPackagesCache = resp;
        return Promise.resolve(resp);
      })
      .catch((err) => Promise.reject(err));
  },
  [types.GET_CONSULTANT_COMMISSION_RATES]: ({}, consultantId) => {
    return getClientInstance()
      .get(`/api/planned_okr/consultant_commission_rates/${consultantId}`)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
  [types.GET_WEEKLY_TRACKING]: ({}, payload) => {
    return getClientInstance()
      .post("/api/realized_okr/weekly_tracking", payload)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
  [types.GET_CONSTANCY_TABLE]: ({}, payload) => {
    return getClientInstance()
      .post("/api/realized_okr/constancy_table", payload)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
  [types.SAVE_ACTION_PLAN]: ({}, payload) => {
    return getClientInstance()
      .put("/api/realized_okr/action_plan", payload)
      .then((resp) => Promise.resolve(resp))
      .catch((err) => Promise.reject(err));
  },
};
